import * as axiosOriginalModule from 'axios-original'

const axiosOriginal =
  axiosOriginalModule.default?.default ||
  axiosOriginalModule.default ||
  axiosOriginalModule

const normalizeEnvUrl = value => (value || '').trim().replace(/^['"]|['"]$/g, '').replace(/\/+$/, '')

const API_BASE_URLS = [
  { apiVersion: 'api7', baseUrl: normalizeEnvUrl(process.env.REACT_APP_BASE_URL_UTAMA_PHP7) },
  { apiVersion: 'api5', baseUrl: normalizeEnvUrl(process.env.REACT_APP_BASE_URL_UTAMA) },
  { apiVersion: 'apisatset', baseUrl: normalizeEnvUrl(process.env.REACT_APP_BASE_URL_SATUSEHAT) },
  { apiVersion: 'apibpjs', baseUrl: normalizeEnvUrl(process.env.REACT_APP_BASE_URL_BPJS) },
  { apiVersion: 'apidokter', baseUrl: normalizeEnvUrl(process.env.REACT_APP_BASE_URL_DOKTER) },
].filter(item => item.baseUrl)

const buildAbsoluteUrl = config => {
  const url = config?.url || ''
  const baseURL = config?.baseURL || ''

  try {
    return new URL(url, baseURL || window.location.origin).toString()
  } catch {
    return url
  }
}

const getMatchedApi = config => {
  const absoluteUrl = buildAbsoluteUrl(config).replace(/\/+$/, '')

  return API_BASE_URLS.find(({ baseUrl }) => absoluteUrl === baseUrl || absoluteUrl.startsWith(`${baseUrl}/`))
}

const buildMutationUrl = (config, matchedApi) => {
  const absoluteUrl = buildAbsoluteUrl(config)
  const parsedUrl = new URL(absoluteUrl, window.location.origin)
  const parsedBaseUrl = new URL(matchedApi.baseUrl, window.location.origin)
  const basePath = parsedBaseUrl.pathname.replace(/\/+$/, '')
  const relativePath = parsedUrl.pathname.replace(basePath, '').replace(/^\/+/, '')

  return `${relativePath}${parsedUrl.search}`
}

const parseRequestData = data => {
  if (typeof data !== 'string') return data || {}

  try {
    return JSON.parse(data)
  } catch {
    return data
  }
}

const buildMutationParams = config => {
  const method = (config?.method || 'get').toLowerCase()

  if (method === 'get' || method === 'delete') {
    return config?.params || {}
  }

  return parseRequestData(config?.data)
}

const createAxiosResponse = ({ config, data }) => ({
  data,
  status: 200,
  statusText: 'OK',
  headers: {},
  config,
  request: null,
})

const axiosProxy = axiosOriginal.create()

axiosProxy.interceptors.request.use(
  config => {
    const nextConfig = {...config}

    nextConfig.headers = {
      ...nextConfig.headers,
    }

    const matchedApi = getMatchedApi(nextConfig)

    if (matchedApi) {
      nextConfig.adapter = async adapterConfig => {
        const { globalMutation } = await import('../utils/useFetch')
        const mutationUrl = buildMutationUrl(adapterConfig, matchedApi)
        const mutationParams = buildMutationParams(adapterConfig)
        const mutationOptions = {
          method: (adapterConfig.method || 'GET').toUpperCase(),
        }

        const data = await globalMutation({
          mutationApiVersion: matchedApi.apiVersion,
          mutationUrl,
          mutationParams,
          mutationOptions,
          timeout: adapterConfig.timeout,
        })

        return createAxiosResponse({ config: adapterConfig, data })
      }
    }

    return nextConfig
  },
  error => Promise.reject(error)
)

axiosProxy.interceptors.response.use(
  response => response,
  error => Promise.reject(error)
)

Object.assign(axiosProxy, {
  Axios: axiosOriginal.Axios,
  AxiosError: axiosOriginal.AxiosError,
  Cancel: axiosOriginal.Cancel,
  CancelToken: axiosOriginal.CancelToken,
  CanceledError: axiosOriginal.CanceledError,
  VERSION: axiosOriginal.VERSION,
  all: axiosOriginal.all,
  formToJSON: axiosOriginal.formToJSON,
  getAdapter: axiosOriginal.getAdapter,
  isAxiosError: axiosOriginal.isAxiosError,
  isCancel: axiosOriginal.isCancel,
  spread: axiosOriginal.spread,
  toFormData: axiosOriginal.toFormData,
})

export default axiosProxy

export const Axios = axiosOriginal.Axios
export const AxiosError = axiosOriginal.AxiosError
export const Cancel = axiosOriginal.Cancel
export const CancelToken = axiosOriginal.CancelToken
export const CanceledError = axiosOriginal.CanceledError
export const VERSION = axiosOriginal.VERSION
export const all = axiosOriginal.all
export const formToJSON = axiosOriginal.formToJSON
export const getAdapter = axiosOriginal.getAdapter
export const isAxiosError = axiosOriginal.isAxiosError
export const isCancel = axiosOriginal.isCancel
export const spread = axiosOriginal.spread
export const toFormData = axiosOriginal.toFormData
